import type { SxProps, Theme } from '@mui/material';
import {
  Box,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { CardSection, CardSubSection, Show } from '@side/pantry';
import type { ReactNode } from 'react';
import { PerformanceFilterValuesFooter } from '../../FilterValuesFooter/FilterValuesFooter';
import {
  formatAmount,
  getPercentage,
  shouldHidePeriodComparisons,
} from '../../helpers';
import { useReportsRole } from '../../ReportsConfiguration/useReportsRole';
import { usePerformanceReportContext } from '../context/PerformanceReportContext';
import { usePerformanceSnapshot } from '../hooks/usePerformanceSnapshot';
import {
  GoalSubText,
  MetricCard,
  TransactionsGoalPieChart,
} from './PerformanceSnapshot.helpers';

function YearlyGoalRowSkeleton() {
  return (
    <Stack direction="row" sx={{ width: '100%', gap: 1, alignItems: 'center' }}>
      <Skeleton
        variant="rounded"
        animation="wave"
        sx={{ flex: 1, height: 8 }}
      />
      <Skeleton
        variant="rounded"
        animation="wave"
        sx={{ width: 120, height: 20 }}
      />
    </Stack>
  );
}

function AverageSaleGoalRowSkeleton() {
  return (
    <Skeleton
      variant="rounded"
      animation="wave"
      sx={{ width: '75%', maxWidth: 220, height: 20 }}
    />
  );
}

export const PerformanceSnapshot = ({
  viewReportAs,
  sx,
}: {
  viewReportAs: string;
  sx?: SxProps<Theme>;
}) => {
  const { filters } = usePerformanceReportContext();
  const hideGoals = shouldHidePeriodComparisons(filters?.reportingPeriod);
  const { data, isLoading, transactionSidesData } = usePerformanceSnapshot({
    viewReportAs,
  });
  const { isAgent } = useReportsRole();

  const salesGoal = data?.salesValueGoal;
  const salesVolume = data?.salesVolume;
  const salesVolumePercentage = getPercentage(
    salesVolume ?? null,
    salesGoal ?? null,
  );

  const transactionsGoal = data?.sidesGoal;
  const transactions = data?.sides;
  const transactionsPercentage = getPercentage(
    transactions ?? null,
    transactionsGoal ?? null,
  );

  const gciLabel = isAgent ? 'My Weighted GCI' : 'Post-Referral GCI';
  const gciValue = isAgent
    ? data?.weightedAdjustedGrossCommission
    : data?.adjustedGrossCommission;

  let volumeGoalRow: ReactNode;
  if (hideGoals) {
    volumeGoalRow = undefined;
  } else if (isLoading) {
    volumeGoalRow = <YearlyGoalRowSkeleton />;
  } else if (salesGoal && salesVolumePercentage != null) {
    volumeGoalRow = (
      <Stack
        direction="row"
        sx={{ width: '100%', gap: 1, alignItems: 'center' }}
      >
        <LinearProgress
          value={salesVolumePercentage}
          variant="determinate"
          color="secondary"
          sx={{ width: '100%' }}
        />
        <Typography
          variant="body2"
          sx={{
            width: 'fit-content',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
          }}
        >
          {salesVolumePercentage}% yearly goal
        </Typography>
      </Stack>
    );
  } else {
    volumeGoalRow = undefined;
  }

  let transactionsGoalRow: ReactNode;
  if (hideGoals) {
    transactionsGoalRow = undefined;
  } else if (isLoading) {
    transactionsGoalRow = <YearlyGoalRowSkeleton />;
  } else if (transactionsGoal) {
    transactionsGoalRow = (
      <Stack
        direction="row"
        sx={{ width: '100%', gap: 1, alignItems: 'center' }}
      >
        <LinearProgress
          value={transactionsPercentage}
          color="secondary"
          variant="determinate"
          sx={{ width: '100%' }}
        />
        <Typography
          variant="body2"
          sx={{
            width: 'fit-content',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
          }}
        >
          {transactionsPercentage}% yearly goal
        </Typography>
      </Stack>
    );
  } else {
    transactionsGoalRow = undefined;
  }

  let averageSaleGoalRow: ReactNode;
  if (hideGoals) {
    averageSaleGoalRow = undefined;
  } else if (isLoading) {
    averageSaleGoalRow = <AverageSaleGoalRowSkeleton />;
  } else if (data?.averageSaleValueGoal) {
    averageSaleGoalRow = (
      <GoalSubText
        goal={data.averageSaleValueGoal}
        actual={data.averageSalesValue}
      />
    );
  }

  return (
    <Box sx={{ ...sx, display: 'flex', height: '100%' }}>
      <CardSection
        cardTitle="Performance Snapshot"
        id="performance-snapshot-card-section"
        sx={{ flex: 1 }}
      >
        <CardSubSection>
          <Stack sx={{ gap: 2 }}>
            <Stack
              sx={{
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
              }}
            >
              <MetricCard
                sx={{ minWidth: 260 }}
                value={salesVolume ? formatAmount(salesVolume) : undefined}
                label="Volume"
                isLoading={isLoading}
                bottomContent={volumeGoalRow}
              />
              <MetricCard
                value={gciValue ? formatAmount(gciValue) : undefined}
                label={gciLabel}
                isLoading={isLoading}
              />
            </Stack>
            <Stack sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <MetricCard
                sx={{ minWidth: 260 }}
                value={transactions ? transactions.toString() : undefined}
                label="Total Transactions"
                isLoading={isLoading}
                rightContent={
                  <Show
                    when={
                      !isLoading &&
                      (transactionSidesData.buySides > 0 ||
                        transactionSidesData.sellSides > 0)
                    }
                  >
                    <TransactionsGoalPieChart
                      buySides={transactionSidesData.buySides}
                      sellSides={transactionSidesData.sellSides}
                    />
                  </Show>
                }
                bottomContent={transactionsGoalRow}
              />
              <MetricCard
                value={
                  data?.averageSalesValue
                    ? formatAmount(data.averageSalesValue)
                    : undefined
                }
                label="Average Sale Value"
                isLoading={isLoading}
                bottomContent={averageSaleGoalRow}
              />
            </Stack>
            <PerformanceFilterValuesFooter sx={{ pt: { xs: 0, md: 3 } }} />
          </Stack>
        </CardSubSection>
      </CardSection>
    </Box>
  );
};
